import { useState, useEffect, useId, useRef } from "react";
import { api, setToken } from "../lib/api";
import { SESSION_KEY, clearSession } from "../lib/session";
import { CONTACT_EMAIL } from "../lib/landingContent";
import { ProductConsole, PublicBrand } from "./PublicBrand";
import "../styles/sarvam-public.css";

function AccountForm({ signup, onSuccess, onNavigate }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const errorId = useId();
  const fieldId = useId();
  const errorRef = useRef(null);
  useEffect(() => {
    if (error && !busy) errorRef.current?.focus();
  }, [error, busy]);
  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const result = signup
        ? await api.register(email.trim(), password, name.trim())
        : await api.login(email.trim(), password);
      if (!result?.access_token) throw new Error("No sign-in session was returned. Please try again.");
      setToken(result.access_token);
      localStorage.setItem(SESSION_KEY, "1");
      onSuccess();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  }
  const canSubmit = Boolean(email.trim() && password && (!signup || (name.trim().length >= 2 && password.length >= 8)));
  function preview() {
    clearSession();
    localStorage.setItem(SESSION_KEY, "demo");
    onSuccess();
  }
  return <main className="wc-auth">
    <header className="wc-auth-header"><PublicBrand onClick={()=>onNavigate("landing")}/><a href={`mailto:${CONTACT_EMAIL}`}>Contact</a></header>
    <div><div className="wc-auth-card">
      <aside className="wc-auth-art" aria-hidden="true">
        <ProductConsole scene="operators" />
      </aside>
      <form className="wc-account-form" onSubmit={submit} aria-label={signup?"Create account":"Sign in"} aria-busy={busy} aria-describedby={error?errorId:undefined}>
        <button className="wc-auth-close" type="button" aria-label="Back to home" disabled={busy} onClick={()=>onNavigate("landing")}>×</button>
        <h1>{signup ? "Create account" : "Sign in"}</h1>
        <p className="wc-auth-intro">{signup ? "Set up your workspace." : "Continue to your workspace."}</p>
        {signup && <label className="wc-auth-field" htmlFor={`${fieldId}-name`}><span>Full name</span><input id={`${fieldId}-name`} required minLength={2} autoComplete="name" placeholder="Your name" value={name} disabled={busy} onChange={event=>setName(event.target.value)}/></label>}
        <label className="wc-auth-field" htmlFor={`${fieldId}-email`}><span>Email address</span><input id={`${fieldId}-email`} type="email" required autoComplete="email" placeholder="you@company.com" value={email} disabled={busy} onChange={event=>setEmail(event.target.value)}/></label>
        <div className="wc-auth-field"><label htmlFor={`${fieldId}-password`} style={{display:"block",marginBottom:7}}>Password</label><div className="wc-auth-password"><input id={`${fieldId}-password`} type={showPassword?"text":"password"} required minLength={signup?8:undefined} autoComplete={signup?"new-password":"current-password"} placeholder={signup?"Create a password":"Enter your password"} value={password} disabled={busy} aria-describedby={signup?`${fieldId}-help`:undefined} onChange={event=>setPassword(event.target.value)}/><button type="button" disabled={busy} aria-label={showPassword?"Hide password":"Show password"} aria-pressed={showPassword} onClick={()=>setShowPassword(!showPassword)}>{showPassword?"Hide":"Show"}</button></div></div>
        {signup && <p className="wc-auth-help" id={`${fieldId}-help`}>Use at least 8 characters.</p>}
        {error && <p className="wc-auth-error" id={errorId} ref={errorRef} tabIndex={-1} role="alert">{error}</p>}
        <button className="wc-button wc-button-dark wc-auth-submit" type="submit" disabled={busy || !canSubmit}>{busy ? (signup ? "Creating account…" : "Signing in…") : (signup ? "Create account" : "Sign in")}</button>
        <p className="wc-auth-switch">{signup ? "Already have an account?" : "New to WeCrew?"}<button type="button" disabled={busy} onClick={()=>onNavigate(signup?"signin":"signup")}>{signup ? "Sign in" : "Sign up"}</button></p>
        <div className="wc-auth-divider">or</div>
        <button className="wc-auth-preview" type="button" disabled={busy} onClick={preview}>Explore without an account</button>
      </form>
    </div></div>
  </main>;
}

export function SignUpPage(props) { return <AccountForm {...props} signup/>; }
export function SignInPage(props) { return <AccountForm {...props} signup={false}/>; }
export function AuthGate({ onEnter, initialMode = "signup" }) {
  const [mode, setMode] = useState(initialMode);
  return mode === "signin" ? <SignInPage onSuccess={onEnter} onNavigate={setMode}/> : <SignUpPage onSuccess={onEnter} onNavigate={setMode}/>;
}
